import { useEffect, useRef, useState } from "react";

/**
 * useReveal — adds the "in" class to an element when it scrolls into view.
 * Keep it tiny: one observer per element, unobserve after reveal.
 *
 * IMPORTANT: default threshold must stay at 0 (any intersection). A ratio
 * threshold like 0.18 can never fire for elements taller than ~viewport/threshold
 * (e.g. the Casting form panel ~5000px on an 812px phone), leaving content at
 * opacity:0 and looking like a giant blank gap.
 */
export default function useReveal(threshold = 0) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || shown) return undefined;

    if (typeof IntersectionObserver === "undefined") {
      setShown(true);
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            io.unobserve(e.target);
          }
        });
      },
      // rootMargin keeps a small "enter the stage" offset without requiring a
      // percentage of the target's own height to be on screen.
      { threshold, rootMargin: "0px 0px -40px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold, shown]);

  return { ref, className: shown ? "reveal in" : "reveal" };
}
